import React, { useState, useEffect } from 'react';
import { medicalDevicesDatabase } from '../data/medical-device-database';

export default function DeviceSelector({ onDeviceSelect }) {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredDevices, setFilteredDevices] = useState([]);
  
  // Initialize filtered devices based on filters
  useEffect(() => {
    // Start with all devices
    let devices = medicalDevicesDatabase.categories.flatMap(cat => 
        cat.devices.map(dev => ({...dev, categoryName: cat.name})) // Add categoryName for potential use
    );

    // Filter by category if selected
    if (selectedCategory) {
      devices = devices.filter(device => 
        medicalDevicesDatabase.categories.find(cat => cat.id === selectedCategory)?.devices.some(d => d.id === device.id)
      );
    }
    
    // Filter by severity if selected
    if (selectedSeverity) {
        devices = devices.filter(device => device.ephiRiskLevel === selectedSeverity);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      devices = devices.filter(device => 
        device.name.toLowerCase().includes(query) ||
        device.manufacturer.toLowerCase().includes(query) ||
        device.description.toLowerCase().includes(query) ||
        device.applications.some(app => app.toLowerCase().includes(query))
      );
    }
    
    setFilteredDevices(devices);
    // React to changes in category, severity, or search query
  }, [selectedCategory, selectedSeverity, searchQuery]); 
  
  const handleDeviceSelect = (device) => {
    setSelectedDevice(device);
    onDeviceSelect(device);
  };
  
  return (
    <div className="bg-white p-6 rounded shadow mb-8">
      <h2 className="text-xl font-semibold mb-4">Select Medical Device</h2>
      
      <div className="mb-4">
        <input
          type="text"
          className="w-full p-2 border rounded mb-4"
          placeholder="Search devices by name, manufacturer, or application..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        
        <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Filter by Category</label>
            <select 
              className="w-full p-2 border rounded"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {medicalDevicesDatabase.categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Filter by Severity</label>
            <select 
              className="w-full p-2 border rounded"
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e.target.value)}
            >
              <option value="">All Severities</option>
              <option value="low">Low Risk</option>
              <option value="medium">Medium Risk</option>
              <option value="high">High Risk</option>
            </select>
          </div>
        </div>
      </div>
      
      <div className="mb-6">
        <h3 className="font-medium mb-2">Device List</h3>
        {filteredDevices.length === 0 ? (
          <p className="text-gray-500 italic">No devices found matching your criteria</p>
        ) : (
          <div className="max-h-96 overflow-y-auto border rounded">
            {filteredDevices.map(device => (
              <div 
                key={device.id} 
                className={`p-3 border-b cursor-pointer hover:bg-blue-50 ${selectedDevice === device ? 'bg-blue-100' : ''}`}
                onClick={() => handleDeviceSelect(device)}
              >
                <div className="font-medium">{device.name}</div>
                <div className="text-sm text-gray-600">{device.manufacturer}</div>
                <div className="text-sm mt-1">{device.description}</div>
                <div className="mt-1">
                  <span className={`text-xs px-2 py-1 rounded ${
                    device.ephiRiskLevel === 'high' ? 'bg-red-100 text-red-800' : 
                    device.ephiRiskLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' : 
                    'bg-green-100 text-green-800'
                  }`}>
                    {device.ephiRiskLevel.toUpperCase()} Risk
                  </span>
                  {device.networkConnected && (
                    <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 ml-2">
                      Network Connected
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {selectedDevice && (
        <div className="p-4 bg-gray-50 rounded border">
          <h3 className="font-medium text-lg mb-2">{selectedDevice.name}</h3>
          <p className="mb-2">{selectedDevice.description}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
            <div>
              <h4 className="font-medium text-sm">Manufacturer</h4>
              <p>{selectedDevice.manufacturer}</p>
            </div>
            <div>
              <h4 className="font-medium text-sm">ePHI Risk Level</h4>
              <p className="capitalize">{selectedDevice.ephiRiskLevel}</p>
            </div>
          </div>
          
          <div className="mb-3">
            <h4 className="font-medium text-sm">Applications</h4>
            <div className="flex flex-wrap gap-1 mt-1">
              {selectedDevice.applications.map((app, index) => (
                <span key={index} className="text-xs px-2 py-1 rounded bg-gray-200">
                  {app}
                </span>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="font-medium text-sm mb-1">Default Threat Profile</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(selectedDevice.threatProfiles).map(([threat, level]) => (
                <div key={threat} className="text-xs flex justify-between p-2 bg-gray-100 rounded">
                  <span className="capitalize">{threat.replace(/([A-Z])/g, ' $1').trim()}:</span>
                  <span className="font-medium">{level}/5</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}